import { ConfirmReserveModal, EventModal } from '_/space-management/components';
import { colors } from '_/space-management/constants/theme';
import { useEventContext } from '_/space-management/hooks/EventContext';
import { useSlotContext } from '_/space-management/hooks/SlotContext';
import { useSpaceContext } from '_/space-management/hooks/SpaceContext';
import { useDateContext } from '_/space-management/hooks/DateContext';
// import useUpdateSlotCoordinates from '_/space-management/hooks/useUpdateSlotCoordinates';
import { SlotProps } from '_/space-management/screens/Workstation/StatusTab/AvailableList/Slot';
import { EventStatus } from '_/space-management/services/models/enums/event-status.enum';
import { RecurrenceType } from '_/space-management/services/models/enums/recurrence-type.enum';
import { SlotsModel } from '_/space-management/services/models/slots.model';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Circle, LayerGroup, useMapEvent } from 'react-leaflet';
import { useNavigation } from '@react-navigation/native';
import { AppRoute } from '_/space-management/navigation/types';
import { startOfDay, endOfDay } from 'date-fns';

interface CustomMarksProps extends SlotProps {
  eventModalVisible: string | undefined;
  setEventModalVisible: React.Dispatch<React.SetStateAction<string | undefined>>;
}

const MapMarker: React.FC<CustomMarksProps> = ({
  slot,
  eventModalVisible,
  setEventModalVisible,
}) => {
  const { currentSpace } = useSpaceContext();
  const { setIsSlotModalVisible, isSlotModalVisible } = useSlotContext();
  // const { updateSlot } = useUpdateSlotCoordinates();
  const { setRecurrenceStr } = useEventContext();
  const navigation = useNavigation<any>();

  const [recurrenceType, setRecurrenceType] = useState<RecurrenceType>(RecurrenceType.ONCE);

  const { getSlotAvailability } = useEventContext();
  const { selectedEventDate } = useDateContext();
  const [slotEvents, setSlotEvents] = useState<any[]>([]);

  useEffect(() => {
    if (eventModalVisible === slot.id) {
      getSlotAvailability(slot.id, startOfDay(selectedEventDate.start), endOfDay(selectedEventDate.end))
        .then((data) => {
          setSlotEvents(
            data?.events?.filter((event: any) => event.status !== EventStatus.CHECKEDOUT) || []
          );
        })
        .catch(() => {});
    }
  }, [eventModalVisible, slot.id, getSlotAvailability, selectedEventDate]);

  // const mapClick = useMapEvent('click', (e) => {
  //   const { lat, lng } = e.latlng;
  //   const coordinates = {
  //     latitude: lat,
  //     longitude: lng,
  //   };

  //   updateSlot(coordinates);
  // });

  const handleConfirmReserve = useCallback(
    (slot: Omit<SlotsModel, 'amenities'>) => {
      setRecurrenceStr('');
      if (currentSpace.scheduleEnabled) {
        navigation.navigate(AppRoute.SLOT_SCHEDULE, { slot });
      } else {
        setIsSlotModalVisible(slot.id);
      }
    },
    [currentSpace.scheduleEnabled, navigation, setIsSlotModalVisible, setRecurrenceStr]
  );

  const handlePressMarker = useCallback(() => {
    if (slot.isAvailable) {
      handleConfirmReserve(slot);
    } else {
      setEventModalVisible(slot.id);
    }
  }, [handleConfirmReserve, setEventModalVisible, slot]);

  const HandleEventModal = useCallback(() => {
    if (slotEvents[0]) {
      return (
        <EventModal
          eventModalVisible={eventModalVisible}
          onBackdropPress={() => setEventModalVisible(undefined)}
          onBackButtonPress={() => setEventModalVisible(undefined)}
          onSwipeStart={() => setEventModalVisible(undefined)}
          id={slotEvents[0]?.slotId}
          name={slotEvents[0]?.user?.name as string}
          source={slotEvents[0].user?.pictureThumbUrl as string}
          startDate={slotEvents[0]?.startAt as unknown as string}
          endDate={slotEvents[0]?.endAt as unknown as string}
        />
      );
    } else {
      return null;
    }
  }, [eventModalVisible, setEventModalVisible, slotEvents]);

  const slotRadius = useMemo(() => {
    if (currentSpace.mapData?.slotRadiusWeb) {
      return currentSpace.mapData.slotRadiusWeb;
    }

    if (currentSpace.mapData?.slotRadius) {
      return currentSpace.mapData?.slotRadius * 0.0000075;
    }

    return 0.07;
  }, [currentSpace.mapData?.slotRadius, currentSpace.mapData?.slotRadiusWeb]);

  if (!slot.mapLocation?.latitude || !slot.mapLocation?.longitude) {
    return null;
  }

  return (
    <LayerGroup>
      <Circle
        pathOptions={{
          fillColor: slot.isAvailable ? colors.successGreen : colors.errorRed,
          color: slot.isAvailable ? colors.successGreen : colors.errorRed,
        }}
        radius={slotRadius}
        center={{ lat: slot.mapLocation.latitude, lng: slot.mapLocation.longitude }}
        stroke={false}
        opacity={1}
        fillOpacity={1}
        eventHandlers={{
          click: handlePressMarker,
        }}
      />

      {isSlotModalVisible === slot.id && (
        <ConfirmReserveModal
          onBackdropPress={() => setIsSlotModalVisible('')}
          onBackButtonPress={() => setIsSlotModalVisible('')}
          onSwipeStart={() => setIsSlotModalVisible('')}
          slot={slot}
          recurrenceType={recurrenceType}
          setRecurrenceType={setRecurrenceType}
        />
      )}

      <HandleEventModal />
    </LayerGroup>
  );
};

export default MapMarker;
